// The real backend adapter for 환자 관리 (admin-api PatientController). Selected by
// VITE_USE_MOCK_API=false — see patient.adapter.ts — and maps the server error envelope
// { error: { code, message, fields, meta } } to PatientApiError.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';
import {
  PatientApiError,
  type ExportJobResult,
  type ExportPatientsParams,
  type ListPatientsParams,
  type ListPatientsResult,
  type Patient,
  type PatientApi,
  type PatientApiErrorCode,
  type PatientAssignedStaff,
  type PatientCloseReason,
  type PatientCreateResult,
  type PatientMedication,
  type PatientStatus,
  type PatientStatusFilter,
  type RegisterPatientInput,
  type UpdatePatientInput,
} from '../types/patient.types';

interface ApiEnvelope<T> {
  data: T;
}

interface ServerErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string>;
    meta?: Record<string, unknown>;
  };
}

function mapError(e: unknown): never {
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  const payload = candidate.response?.data?.error;
  throw new PatientApiError(
    (payload?.code as PatientApiErrorCode) ?? 'UNKNOWN',
    payload?.message ?? '알 수 없는 오류가 발생했습니다.',
    { fields: payload?.fields, meta: payload?.meta },
  );
}

// ── Status wire-casing (F1) ─────────────────────────────────────────────────
// The BE uses LOWERCASE status on the wire ('active' | 'closed' | 'withdrawn',
// plus the 'ending' list filter); the FE enum + UI + i18n keys are UPPERCASE.
// Normalize at this HTTP boundary so the rest of the FE keeps working off the
// uppercase PatientStatus enum without a sweeping rename. closeReason is upper-
// cased defensively (it drives the closed-row badge + panel field the same way).
const STATUS_FILTER_TO_WIRE: Record<Exclude<PatientStatusFilter, 'all'>, string> = {
  ACTIVE: 'active',
  CLOSED: 'closed',
  WITHDRAWN: 'withdrawn',
  ending: 'ending',
};

function statusFromWire(value: unknown): PatientStatus {
  switch (String(value).toLowerCase()) {
    case 'closed':
      return 'CLOSED';
    case 'withdrawn':
      return 'WITHDRAWN';
    default:
      return 'ACTIVE';
  }
}

function closeReasonFromWire(value: unknown): PatientCloseReason | null {
  if (value == null || value === '') return null;
  return String(value).toUpperCase() as PatientCloseReason;
}

// SHCT-794 — the wire codes the app-user type as SELF / GUARDIAN, the same pair `registerPatient`
// sends outbound; fold it back to the feature's own lowercase union so one representation is used
// everywhere. Left undefined when the field is absent, which is how the panel knows not to render the
// 앱 사용자 유형 and guardian rows at all (`PatientDetailResponse` does not carry them yet).
function appUserTypeFromWire(value: unknown): Patient['appUserType'] {
  if (value == null || value === '') return undefined;
  return String(value).toUpperCase() === 'GUARDIAN' ? 'guardian' : 'patient';
}

/**
 * The wire's 처방약 rows, narrowed to the pairs the panel can render (SHCT-738 · SHCT-737).
 *
 * `undefined` when the field is absent or is not an array at all — the panel reads that as "this server
 * does not serve the list" and falls back to the superseded single pair. An EMPTY array is preserved as
 * an empty array, because "this patient has no prescriptions" is a real answer and must not be mistaken
 * for "the server never told us".
 *
 * A row with no 약품명 is dropped: 복약주기 alone has nothing to attach to, and PRD 02 §3.7 makes the
 * drug the required half of the pair. A missing 복약주기 becomes '' — a legitimate saved state.
 */
function medicationsFromWire(rows: unknown): PatientMedication[] | undefined {
  if (!Array.isArray(rows)) return undefined;
  return rows.flatMap((r) => {
    const row = r as { name?: unknown; freq?: unknown } | null;
    const name = typeof row?.name === 'string' ? row.name.trim() : '';
    if (!name) return [];
    return [{ name, freq: typeof row?.freq === 'string' ? row.freq.trim() : '' }];
  });
}

/**
 * 담당 의료진 (SHCT-981), kept in the server's order — 주담당 first, then by assignment time and id; the
 * FIRST entry is the 주담당 and the panel never re-sorts. `undefined` when the field is absent (an older
 * server, or the list response), which leaves the panel row out. A deleted staff account keeps its row with
 * a null name, so an edit round-trip cannot silently drop the assignment.
 *
 * The 주담당 is moved to the front defensively (a stable sort, so the rest keep the server's order): the
 * panel treats position 0 as the 주담당 and sends the list back in that order.
 */
function assignedStaffFromWire(rows: unknown): PatientAssignedStaff[] | undefined {
  if (!Array.isArray(rows)) return undefined;
  return rows
    .flatMap((r) => {
      const row = r as { id?: unknown; name?: unknown; primary?: unknown } | null;
      if (typeof row?.id !== 'number') return [];
      const name = typeof row.name === 'string' && row.name.trim() ? row.name : null;
      return [{ id: row.id, name, primary: row.primary === true }];
    })
    .sort((a, b) => Number(b.primary) - Number(a.primary));
}

// BE patient → FE patient: lift status / closeReason to the uppercase enum, narrow the 처방약 and 담당
// 의료진 lists. Name and phone fields pass through as received — the detail response is unmasked
// (SHCT-981) and the list response masked; neither is re-masked or unmasked here.
function patientFromWire(p: Patient): Patient {
  return {
    ...p,
    status: statusFromWire(p.status),
    closeReason: closeReasonFromWire(p.closeReason),
    appUserType: appUserTypeFromWire(p.appUserType),
    medications: medicationsFromWire(p.medications),
    assignedStaff: assignedStaffFromWire(p.assignedStaff),
  };
}

// FE list/export query → wire: send the uppercase status filter as lowercase.
function toWireQuery<T extends ListPatientsParams | ExportPatientsParams>(params: T) {
  return params.status
    ? { ...params, status: STATUS_FILTER_TO_WIRE[params.status] }
    : params;
}

export const patientHttpApi: PatientApi = {
  async listPatients(params: ListPatientsParams): Promise<ListPatientsResult> {
    try {
      const res = await httpClient.get<ApiEnvelope<ListPatientsResult>>(
        endpoints.patients,
        { params: toWireQuery(params) },
      );
      const data = res.data?.data;
      if (!data || typeof data !== 'object' || !Array.isArray(data.items)) {
        throw new PatientApiError('UNKNOWN', '환자 목록을 불러올 수 없습니다.');
      }
      return { ...data, items: data.items.map(patientFromWire) };
    } catch (e) {
      mapError(e);
    }
  },

  // FEATURE_062: the backend streams a Root-only CSV of raw PHI. Download it as a Blob (responseType:'blob')
  // — the FE no longer builds the CSV client-side. The auth interceptor attaches the Bearer token.
  async exportPatients(params: ExportPatientsParams): Promise<Blob> {
    try {
      const res = await httpClient.get(`${endpoints.patients}/export`, {
        params: toWireQuery(params),
        responseType: 'blob',
      });
      return res.data as Blob;
    } catch (e) {
      mapError(e);
    }
  },

  // FEATURE_062 async large-file export: queue a background job (202). The server emails the requester a
  // token-gated download link when the file is ready; same filters as the list, sent as query params.
  async requestExportJob(params: ExportPatientsParams): Promise<ExportJobResult> {
    try {
      const res = await httpClient.post<ApiEnvelope<ExportJobResult>>(
        `${endpoints.patients}/export-jobs`,
        null,
        { params: toWireQuery(params) },
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async getPatient(id: number): Promise<Patient> {
    try {
      const res = await httpClient.get<ApiEnvelope<Patient>>(
        `${endpoints.patients}/${id}`,
      );
      return patientFromWire(res.data.data);
    } catch (e) {
      mapError(e);
    }
  },

  // FEATURE_014 (Root-only registration). Maps the FE app-user-type to the backend wire codes (SELF/GUARDIAN)
  // and sends the flat PatientCreateRequest shape. Returns the server-assigned identity.
  async registerPatient(input: RegisterPatientInput): Promise<PatientCreateResult> {
    try {
      const body = {
        ...input,
        appUserType: input.appUserType === 'guardian' ? 'GUARDIAN' : 'SELF',
      };
      const res = await httpClient.post<ApiEnvelope<PatientCreateResult>>(
        endpoints.patients,
        body,
      );
      const created = res.data.data;
      return { ...created, status: statusFromWire(created.status) };
    } catch (e) {
      mapError(e);
    }
  },

  // FEATURE_015 PUT /admin/patients/{id} (BE PatientController#update, `PATIENT:WRITE`) — the panel's ONE
  // write per [저장] (SHCT-981). The body carries only the changed fields; the backend reads an absent key
  // as "leave alone" and validates the lot in one transaction. Returns 200 with a null payload; refusals
  // arrive as a single `error.code` (409 PATIENT_DUPLICATE_MRN carries meta {uid, maskedName, statusLabel}).
  async updatePatient(id: number, input: UpdatePatientInput): Promise<void> {
    try {
      await httpClient.put(`${endpoints.patients}/${id}`, input);
    } catch (e) {
      mapError(e);
    }
  },

  // [US-PM-07] POST /admin/patients/{id}/resend-sms (BE PatientController#resendSms, Root-only). Returns
  // 200 with a null payload on success; a gateway failure comes back as 409 PATIENT_SMS_SEND_FAILED,
  // which mapError lifts into a PatientApiError the failure popup re-renders.
  async resendInstallSms(patientId: number): Promise<void> {
    try {
      await httpClient.post(`${endpoints.patients}/${patientId}/resend-sms`);
    } catch (e) {
      mapError(e);
    }
  },
};
